/**
 * Site identity resolution from hostname.
 *
 * URL pattern: <space-slug>--<tenant-slug>.site.<platform-domain>
 * Example: main-site--tenant1.site.llt-map.justbit.click
 *
 * The "--" separator encodes both space and tenant into a single subdomain level,
 * staying within the coverage of the ACM wildcard *.site.<platform-domain>.
 *
 * For local development, falls back to VITE_TENANT_SLUG / VITE_SPACE_SLUG env vars.
 */

interface SiteIdentifiers {
  spaceSlug: string;
  tenantSlug: string;
}

let cached: SiteIdentifiers | null = null;

export function getSiteIdentifiers(): SiteIdentifiers {
  if (cached) return cached;

  const envSpace = (import.meta.env.VITE_SPACE_SLUG as string | undefined) || undefined;
  const envTenant = (import.meta.env.VITE_TENANT_SLUG as string | undefined) || undefined;

  const host = window.location.hostname;
  const parts = host.split('.');

  // Production URL pattern: <space>--<tenant>.site.<base-domain>
  // parts[0] = "<space>--<tenant>" (e.g. "main-site--tenant1")
  // parts[1] = "site" (fixed marker)
  if (parts.length >= 3 && parts[1] === 'site' && parts[0] !== 'localhost') {
    const subdomain = parts[0];
    const sep = subdomain.indexOf('--');
    if (sep !== -1) {
      cached = {
        spaceSlug: envSpace ?? subdomain.slice(0, sep),
        tenantSlug: subdomain.slice(sep + 2),
      };
    } else {
      // Fallback: tenant-only subdomain, no explicit space in URL
      cached = {
        tenantSlug: subdomain,
        spaceSlug: envSpace ?? 'default',
      };
    }
    return cached;
  }

  // Local dev fallback — parts[0] is 'localhost' in normal dev, not a valid tenant slug.
  // Use VITE_TENANT_SLUG / VITE_SPACE_SLUG env vars (set in apps/website/.env).
  cached = {
    tenantSlug: envTenant ?? 'tenant1',
    spaceSlug: envSpace ?? 'default',
  };
  return cached;
}

export function getTenantSlug(): string {
  return getSiteIdentifiers().tenantSlug;
}

export function getSpaceSlug(): string {
  return getSiteIdentifiers().spaceSlug;
}
